import {Context} from 'openfin-fdc3';

import {CustomError} from './Errors';

/**
 * Defines the format of the response that must be returned by a search engine endpoint.
 */
export type SearchResponse = SearchResult[];

/**
 * Defines a single search result. Each result will be displayed to the user, as part of a scrollable list.
 */
export type SearchResult = SearchResultSimpleText | SearchResultContactCard;

export enum ResultTemplate {
    SIMPLE_TEXT,
    CONTACT_CARD
}

interface SearchResultBase {
    template: ResultTemplate;

    title: string;
    description: string;
    icon: string;
    action: EngineAction;
}

export interface SearchResultSimpleText extends SearchResultBase {
    template: ResultTemplate.SIMPLE_TEXT;
}

export interface SearchResultContactCard extends SearchResultBase {
    template: ResultTemplate.CONTACT_CARD;

    subtitle: string;
    data1: string;
    data2: string;
    data3: string;
}

/**
 * Defines all the possible actions that can be performed when the user clicks a search result.
 */
export type EngineAction = ApplicationAction | BrowserAction | IntentAction;

/**
 * Clicking this result should launch an application.
 */
export interface ApplicationAction {
    type: 'application';

    /**
     * URL of the application that should be launched.
     *
     * If the application is already running, it will receive a `run-requested` event.
     */
    manifestUrl: string;
}

/**
 * Clicking this result should open a web page in the user's default browser.
 */
export interface BrowserAction {
    type: 'browser';

    /**
     * The URL to open
     */
    url: string;
}

/**
 * Will raise an FDC3 intent, arguments correspond to those of `fdc3.raiseIntent`.
 */
export interface IntentAction {
    type: 'intent';

    /**
     * The intent name to raise. This (along with the type of the `context` passed) determines which application(s) are
     * capable of handling this intent.
     */
    intent: string;

    /**
     * The context that will be sent with this intent. This data will be passed to the application that is chosen to
     * handle the intent.
     */
    context: Context;

    /**
     * The name of an application within the directory that should handle this intent. If omitted, FDC3 will search the
     * users app directory for a compatible application.
     *
     * If `target` is specified but the given application cannot be found within the users directory, then this
     * search result will have no effect when clicked.
     */
    target?: string;
}

/**
 * Represents an error that occurred when attempting to query a search engine.
 */
export class SearchEngineError extends CustomError {
    constructor(message: string, innerError: Error | undefined, url: string) {
        super('SearchEngineError', message, innerError, {url});
    }
}
